/* eslint-disable @typescript-eslint/no-explicit-any */

import type { Dispatch } from "redux";

export type DispatchAction = Dispatch<Action<string>>;
export type Action<TType extends string> = Readonly<{
  type: TType;
  scope: string;
  partner: string;
}>;

export type AnyAction = Action<any>;

export type ErrorAction<T extends string> = Action<T> &
  Readonly<{
    error: Error;
  }>;

export type SuccessAction<T extends string> = Action<T>;

export type AsyncActionCreator<
  TStartType extends string,
  TSuccessType extends string,
  TFailureType extends string,
  StartAC extends (...args: any[]) => any,
  SuccessAC extends (...args: any[]) => any,
  FailureAC extends (...args: any[]) => { error: Error },
> = {
  start: PayloadActionCreator<TStartType, Parameters<StartAC>, ReturnType<StartAC>>;
  success: PayloadActionCreator<TSuccessType, Parameters<SuccessAC>, ReturnType<SuccessAC>>;
  error: PayloadActionCreator<TFailureType, Parameters<FailureAC>, ReturnType<FailureAC>>;
};

export interface EmptyActionCreator<TType extends string> {
  (): Action<TType>;
  getType(): TType;
  getPartner(): string;
}
export interface PayloadActionCreator<TType extends string, TArgs extends any[], TPayload = void> {
  (...args: TArgs): Action<TType> & TPayload;
  getType(): TType;
  getPartner(): string;
}
export function createAction<TType extends string>(
  type: TType,
  scope: string,
  partnerId: string
): EmptyActionCreator<TType>;

// eslint-disable-next-line no-redeclare
export function createAction<
  TType extends string,
  AC extends (...args: any[]) => any,
  Payload extends ReturnType<AC>,
>(
  type: TType,
  scope: string,
  partnerId: string,
  creatorFunction?: AC
): PayloadActionCreator<TType, Parameters<AC>, Payload>;

/**
 * @param type
 * @param scope
 * @param partnerId
 * @param creatorFunction
 */
// eslint-disable-next-line no-redeclare
export function createAction<
  TType extends string,
  AC extends (...args: any[]) => any,
  Payload extends ReturnType<AC>,
>(
  type: TType,
  scope: string,
  partnerId: string,
  creatorFunction?: AC
): EmptyActionCreator<TType> | PayloadActionCreator<TType, Parameters<AC>, Payload> {
  return creatorFunction
    ? Object.assign(
        (...args: any[]) => {
          const payload = creatorFunction(...args);
          return { type, scope, partner: partnerId, ...payload };
        },
        { getType: () => type, getPartner: () => partnerId }
      )
    : Object.assign(() => ({ type, scope, partner: partnerId }), {
        getType: () => type,
        getPartner: () => partnerId,
      });
}

/**
 * @deprecated Preferably use `createAsyncActionFromBaseType`.
 */
export function createAsyncAction<
  TStartType extends string,
  TSuccessType extends string,
  TFailureType extends string,
  StartAC extends (...args: any[]) => any,
  SuccessAC extends (...args: any[]) => any,
  FailureAC extends (...args: any[]) => { error: Error },
>(
  scope: string,
  partnerId: string,
  startType: TStartType,
  startCreatorFunction: StartAC,
  successType: TSuccessType,
  successCreatorFunction: SuccessAC,
  failureType: TFailureType,
  failureCreatorFunction: FailureAC
): AsyncActionCreator<TStartType, TSuccessType, TFailureType, StartAC, SuccessAC, FailureAC> {
  return {
    start: Object.assign(
      (...args: Parameters<StartAC>) => {
        const payload = startCreatorFunction(...args);
        return { type: startType, scope, partner: partnerId, ...payload };
      },
      { getType: () => startType, getPartner: () => partnerId }
    ),
    success: Object.assign(
      (...args: Parameters<SuccessAC>) => {
        const payload = successCreatorFunction(...args);
        return { type: successType, scope, partner: partnerId, ...payload };
      },
      { getType: () => successType, getPartner: () => partnerId }
    ),
    error: Object.assign(
      (...args: Parameters<FailureAC>) => {
        // the error constraint is necessary in the failureCreatorFunction return
        // type to ensure errors are always present but needs to be any here to
        // un-narrow the output return type (which could be anything)
        const payload: any = failureCreatorFunction(...args);
        return { type: failureType, scope, partner: partnerId, ...payload };
      },
      { getType: () => failureType, getPartner: () => partnerId }
    ),
  };
}

/**
 * Helper methods to create partner specific action creators for convenience
 */

export const createActionCreator =
  (partnerId: string) =>
  <TType extends string, AC extends (...args: any[]) => any>(
    type: TType,
    scope: string,
    creatorFunction?: AC
  ) =>
    createAction(type, scope, partnerId, creatorFunction);

export const createAsyncActionCreator =
  (partnerId: string) =>
  <
    TStartType extends string,
    TSuccessType extends string,
    TFailureType extends string,
    StartAC extends (...args: any[]) => any,
    SuccessAC extends (...args: any[]) => any,
    FailureAC extends (...args: any[]) => { error: Error },
  >(
    scope: string,
    startType: TStartType,
    startCreatorFunction: StartAC,
    successType: TSuccessType,
    successCreatorFunction: SuccessAC,
    failureType: TFailureType,
    failureCreatorFunction: FailureAC
  ) =>
    createAsyncAction(
      scope,
      partnerId,
      startType,
      startCreatorFunction,
      successType,
      successCreatorFunction,
      failureType,
      failureCreatorFunction
    );
